import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, ImagePlus, KeyRound, Loader2, Plus, Save, Trash2, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { productImage, isCustomImage, IMAGES } from "@/lib/images";

const STATUS_OPTS = ["undetected", "testing", "updating", "down"];
const TIER_LABELS = { day: "1 DAY", week: "1 WEEK", "15day": "15 DAYS", month: "1 MONTH", "3month": "3 MONTHS", lifetime: "LIFETIME", instant: "INSTANT" };
const TIER_KEYS = Object.keys(TIER_LABELS);
const PRESET_KEYS = Object.keys(IMAGES);

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const isoToLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const inputCls = "w-full bg-transparent border border-white/12 px-3 py-2 font-mono text-sm focus:border-white focus:outline-none";
const labelCls = "font-mono text-[9px] tracking-[0.15em] text-white/40";

// ---------------------------------------------------------------- image picker (upload custom or pick preset)
function ImagePicker({ value, onChange, tid }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
      toast.error("JPG, PNG, WEBP or GIF only");
      return;
    }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8 MB)"); return; }
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, {
        onUploadProgress: (e) => setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
      });
      onChange(data.url);
      toast.success("Image uploaded — hit SAVE to apply it");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    }
    setUploading(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-4" data-testid={`image-picker-${tid}`}>
      <div className="w-28 h-[72px] rounded-xl border border-white/15 overflow-hidden bg-black shrink-0">
        <img src={productImage(value)} alt="" data-testid={`image-preview-${tid}`}
          className="w-full h-full object-cover"
          style={isCustomImage(value) ? undefined : { filter: "grayscale(100%) brightness(50%) contrast(120%)" }} />
      </div>
      <div className="flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
            data-testid={`image-file-${tid}`}
            onChange={(e) => { upload(e.target.files && e.target.files[0]); e.target.value = ""; }} />
          <button type="button" onClick={() => fileRef.current && fileRef.current.click()} disabled={uploading}
            data-testid={`image-upload-${tid}`}
            className="btn-ghost px-4 py-2 text-[10px] inline-flex items-center gap-2 disabled:opacity-60">
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <ImagePlus size={12} />}
            {uploading ? `UPLOADING ${progress}%` : "UPLOAD CUSTOM IMAGE"}
          </button>
          <select value={PRESET_KEYS.includes(value) ? value : ""}
            onChange={(e) => e.target.value && onChange(e.target.value)}
            data-testid={`image-preset-${tid}`}
            className="bg-black border border-white/12 px-3 py-2 font-mono text-[10px] tracking-[0.15em] focus:border-white focus:outline-none">
            <option value="">PRESET ART…</option>
            {PRESET_KEYS.map((k) => <option key={k} value={k}>{k.replace(/_/g, " ").toUpperCase()}</option>)}
          </select>
        </div>
        {uploading && (
          <div className="h-1 bg-white/10 w-full" data-testid={`upload-progress-${tid}`}>
            <div className="h-1 bg-white transition-all duration-200" style={{ width: `${progress}%` }} />
          </div>
        )}
        <p className="font-mono text-[9px] tracking-[0.12em] text-white/30">JPG / PNG / WEBP / GIF · MAX 8 MB · CUSTOM IMAGES SHOW AS-IS, PRESETS STAY MONOCHROME</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- key pool panel
function KeysPanel({ product }) {
  const [data, setData] = useState(null);
  const [tier, setTier] = useState(product.tiers[0]?.tier || "day");
  const [bulk, setBulk] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/products/${product.slug}/keys`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [product.slug]);

  const addKeys = async () => {
    const keys = bulk.split("\n").map((k) => k.trim()).filter(Boolean);
    if (!keys.length) { toast.error("Paste at least one key"); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post(`/admin/products/${product.slug}/keys`, { tier, keys });
      toast.success(`${res.added} key(s) added${res.skipped ? ` · ${res.skipped} duplicate(s) skipped` : ""}`);
      setBulk("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add keys");
    }
    setBusy(false);
  };

  const removeKey = async (id) => {
    try {
      await api.delete(`/admin/keys/${id}`);
      toast.success("Key removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Remove failed");
    }
  };

  return (
    <div className="mt-5 border-t border-white/10 pt-5" data-testid={`keys-panel-${product.slug}`}>
      <div className="flex flex-wrap gap-2 mb-4">
        {product.tiers.map((t) => {
          const c = (data && data.counts && data.counts[t.tier]) || { available: 0, assigned: 0 };
          return (
            <span key={t.tier} data-testid={`keys-count-${product.slug}-${t.tier}`}
              className={`font-mono text-[10px] tracking-[0.15em] border px-3 py-1.5 ${c.available === 0 ? "border-red-500/40 text-red-300/80" : "border-white/15 text-white/60"}`}>
              {TIER_LABELS[t.tier] || t.tier}: {c.available} IN POOL · {c.assigned} SOLD
            </span>
          );
        })}
      </div>

      <div className="grid gap-3 sm:grid-cols-[160px_1fr_auto]">
        <select value={tier} onChange={(e) => setTier(e.target.value)} data-testid={`keys-tier-${product.slug}`}
          className="bg-black border border-white/12 px-3 py-2 font-mono text-xs tracking-[0.15em] focus:border-white focus:outline-none">
          {product.tiers.map((t) => <option key={t.tier} value={t.tier}>{TIER_LABELS[t.tier] || t.tier}</option>)}
        </select>
        <textarea value={bulk} onChange={(e) => setBulk(e.target.value)} rows={3}
          placeholder={"Paste keys here — one per line"}
          data-testid={`keys-input-${product.slug}`}
          className="bg-transparent border border-white/12 px-3 py-2 font-mono text-xs focus:border-white focus:outline-none placeholder:text-white/25" />
        <button onClick={addKeys} disabled={busy} data-testid={`keys-add-${product.slug}`}
          className="btn-inj px-5 py-2 text-xs inline-flex items-center gap-2 self-start disabled:opacity-60">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} ADD KEYS
        </button>
      </div>

      <div className="mt-4 max-h-56 overflow-y-auto rounded-xl border border-white/[0.07]">
        {data && data.keys.length > 0 ? data.keys.map((k) => (
          <div key={k.id} className="flex items-center gap-3 px-3 py-2 border-b border-white/[0.05] font-mono text-xs">
            <span className="text-white/35 w-16 shrink-0">{(TIER_LABELS[k.tier] || k.tier).toUpperCase()}</span>
            <span className="flex-1 truncate text-white/75">{k.key}</span>
            <span className={k.status === "available" ? "text-white/40" : "text-white"}>
              {k.status === "available" ? "IN POOL" : "SOLD"}
            </span>
            {k.status === "available" && (
              <button onClick={() => removeKey(k.id)} data-testid={`key-delete-${k.id}`} className="text-white/40 hover:text-red-400 transition-colors" title="Remove key">
                <Trash2 size={13} />
              </button>
            )}
          </div>
        )) : (
          <div className="px-3 py-6 text-center font-mono text-[10px] tracking-[0.2em] text-white/30">// NO KEYS IN POOL YET</div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- add product form
function AddProductForm({ categories, onCreated, onCancel }) {
  const empty = { name: "", slug: "", category: categories[0]?.slug || "cheat", game: "", tagline: "", image: "", status: "undetected", manage_stock: true, sale_pct: 0, features: "" };
  const [form, setForm] = useState(empty);
  const [slugTouched, setSlugTouched] = useState(false);
  const [tiers, setTiers] = useState([{ tier: "day", price: "", sale_pct: "0" }]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const setTierRow = (i, k, v) => setTiers((prev) => prev.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));

  const submit = async (e) => {
    e.preventDefault();
    const validTiers = tiers.filter((t) => t.tier && parseFloat(t.price) >= 0 && t.price !== "");
    if (!validTiers.length) { toast.error("Add at least one duration with a price"); return; }
    setSaving(true);
    try {
      await api.post("/admin/products", {
        name: form.name.trim(),
        slug: (form.slug || slugify(form.name)).trim(),
        category: form.category,
        game: form.game.trim() || null,
        tagline: form.tagline.trim(),
        image: form.image || null,
        status: form.status,
        manage_stock: form.manage_stock,
        sale_pct: parseFloat(form.sale_pct) || 0,
        features: form.features.split("\n").map((f) => f.trim()).filter(Boolean),
        tiers: validTiers.map((t) => ({
          tier: t.tier,
          amount: Math.round(parseFloat(t.price) * 100),
          sale_pct: Math.min(Math.max(parseFloat(t.sale_pct) || 0, 0), 90),
        })),
      });
      toast.success(`${form.name} created`);
      onCreated();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Create failed");
    }
    setSaving(false);
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-white/25 p-6 space-y-4" data-testid="add-product-form">
      <div className="flex items-center justify-between">
        <div className="font-mono text-xs tracking-[0.2em] text-white/60">// NEW PRODUCT</div>
        <button type="button" onClick={onCancel} data-testid="add-product-cancel" className="text-white/50 hover:text-white"><X size={16} /></button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelCls}>NAME *</label>
          <input required value={form.name} data-testid="add-product-name"
            onChange={(e) => { set("name", e.target.value); if (!slugTouched) set("slug", slugify(e.target.value)); }}
            className={`${inputCls} mt-1`} placeholder="GZ // NEW MENU" />
        </div>
        <div>
          <label className={labelCls}>SLUG (URL) *</label>
          <input required value={form.slug} data-testid="add-product-slug"
            onChange={(e) => { setSlugTouched(true); set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "")); }}
            className={`${inputCls} mt-1`} placeholder="new-menu" />
        </div>
        <div>
          <label className={labelCls}>CATEGORY</label>
          <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="add-product-category"
            className="mt-1 w-full bg-black border border-white/12 px-3 py-2 font-mono text-xs tracking-[0.15em] focus:border-white focus:outline-none">
            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>GAME / LABEL</label>
          <input value={form.game} onChange={(e) => set("game", e.target.value)} data-testid="add-product-game"
            className={`${inputCls} mt-1`} placeholder="Arc Raiders" />
        </div>
        <div>
          <label className={labelCls}>STATUS</label>
          <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="add-product-status"
            className="mt-1 w-full bg-black border border-white/12 px-3 py-2 font-mono text-xs tracking-[0.15em] focus:border-white focus:outline-none">
            {STATUS_OPTS.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>ALL-DURATIONS SALE % (0 = USE PER-DURATION SALES)</label>
          <input type="number" min="0" max="90" step="1" value={form.sale_pct} onChange={(e) => set("sale_pct", e.target.value)}
            data-testid="add-product-sale" className={`${inputCls} mt-1`} placeholder="0" />
        </div>
      </div>

      <div>
        <label className={labelCls}>PRODUCT IMAGE</label>
        <div className="mt-2"><ImagePicker value={form.image} onChange={(v) => set("image", v)} tid="new" /></div>
      </div>

      <div>
        <label className={labelCls}>TAGLINE</label>
        <textarea value={form.tagline} onChange={(e) => set("tagline", e.target.value)} rows={2} data-testid="add-product-tagline"
          className={`${inputCls} mt-1`} placeholder="Short sales pitch shown on the card" />
      </div>
      <div>
        <label className={labelCls}>FEATURES (ONE PER LINE)</label>
        <textarea value={form.features} onChange={(e) => set("features", e.target.value)} rows={3} data-testid="add-product-features"
          className={`${inputCls} mt-1`} placeholder={"Aimbot\nFull ESP\nStream-Proof"} />
      </div>

      <div>
        <label className={labelCls}>DURATIONS & PRICES ($)</label>
        <div className="mt-2 space-y-2">
          {tiers.map((t, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <select value={t.tier} onChange={(e) => setTierRow(i, "tier", e.target.value)} data-testid={`add-tier-select-${i}`}
                className="bg-black border border-white/12 px-3 py-2 font-mono text-xs focus:border-white focus:outline-none">
                {TIER_KEYS.map((k) => <option key={k} value={k}>{TIER_LABELS[k]}</option>)}
              </select>
              <input type="number" step="0.01" min="0" value={t.price} onChange={(e) => setTierRow(i, "price", e.target.value)}
                data-testid={`add-tier-price-${i}`} className={`${inputCls} w-28`} placeholder="PRICE" />
              <input type="number" step="1" min="0" max="90" value={t.sale_pct} onChange={(e) => setTierRow(i, "sale_pct", e.target.value)}
                data-testid={`add-tier-sale-${i}`} className={`${inputCls} w-24`} placeholder="SALE %" title="Sale % for this duration only" />
              <button type="button" onClick={() => setTiers((prev) => prev.filter((_, idx) => idx !== i))} data-testid={`add-tier-remove-${i}`}
                className="text-white/40 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setTiers((prev) => [...prev, { tier: "week", price: "", sale_pct: "0" }])} data-testid="add-tier-row"
          className="btn-ghost mt-3 px-4 py-2 text-[10px] inline-flex items-center gap-2">
          <Plus size={12} /> ADD DURATION
        </button>
      </div>

      <label className="flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] text-white/50 cursor-pointer">
        <input type="checkbox" checked={form.manage_stock} onChange={(e) => set("manage_stock", e.target.checked)} className="accent-white" data-testid="add-product-manage-stock" />
        USE KEY POOL (STOCK = KEYS YOU UPLOAD)
      </label>

      <button type="submit" disabled={saving} data-testid="add-product-submit"
        className="btn-inj px-8 py-3 text-xs inline-flex items-center gap-2 disabled:opacity-60">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} CREATE PRODUCT
      </button>
    </form>
  );
}

// ---------------------------------------------------------------- existing product row
function ProductRow({ p, categories, onSaved }) {
  const [draft, setDraft] = useState(p);
  const [saving, setSaving] = useState(false);
  const [showKeys, setShowKeys] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const set = (k, v) => setDraft((prev) => ({ ...prev, [k]: v }));
  const setTier = (tier, val) => setDraft((prev) => ({
    ...prev,
    tiers: prev.tiers.map((t) => (t.tier === tier ? { ...t, price: val, amount: Math.round(parseFloat(val || 0) * 100) } : t)),
  }));
  const setTierSale = (tier, val) => setDraft((prev) => ({
    ...prev,
    tiers: prev.tiers.map((t) => (t.tier === tier ? { ...t, sale_pct: val } : t)),
  }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/products/${p.slug}`, {
        name: draft.name, tagline: draft.tagline, status: draft.status,
        active: draft.active, manage_stock: draft.manage_stock,
        stock_override: draft.stock_override === "" || draft.stock_override == null ? -1 : parseInt(draft.stock_override, 10),
        vouch_override: draft.vouch_override === "" || draft.vouch_override == null ? -1 : parseInt(draft.vouch_override, 10),
        category: draft.category,
        image: draft.image || null,
        sale_pct: Math.min(Math.max(parseFloat(draft.sale_pct) || 0, 0), 90),
        // always re-normalize to aware UTC ISO — heals legacy timezone-less values
        sale_ends_at: draft.sale_ends_at ? new Date(draft.sale_ends_at).toISOString() : "",
        undetected_since: draft.undetected_since || "",
        loader_url: draft.loader_url || "",
        tutorial_url: draft.tutorial_url || "",
        tiers: draft.tiers.map((t) => ({
          tier: t.tier,
          amount: Math.round(parseFloat(t.price || 0) * 100),
          sale_pct: Math.min(Math.max(parseFloat(t.sale_pct) || 0, 0), 90),
        })),
      });
      toast.success(`${draft.name} saved`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
    setSaving(false);
  };

  const del = async () => {
    if (!confirmDel) {
      setConfirmDel(true);
      setTimeout(() => setConfirmDel(false), 4000);
      return;
    }
    try {
      await api.delete(`/admin/products/${p.slug}`);
      toast.success(`${p.name} deleted`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const tierSaleCount = draft.tiers.filter((t) => parseFloat(t.sale_pct) > 0).length;
  const sale = parseFloat(draft.sale_pct) > 0 || tierSaleCount > 0;

  return (
    <div className="rounded-2xl border border-white/12 p-6" data-testid={`admin-product-${p.slug}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <input value={draft.name} onChange={(e) => set("name", e.target.value)}
          data-testid={`product-name-${p.slug}`}
          className="bg-transparent border border-white/12 px-3 py-2 font-display text-lg uppercase flex-1 min-w-[220px] focus:border-white focus:outline-none" />
        <div className="flex flex-wrap items-center gap-2">
          <select value={draft.status} onChange={(e) => set("status", e.target.value)}
            data-testid={`product-status-${p.slug}`}
            className="bg-black border border-white/12 px-3 py-2 font-mono text-xs tracking-[0.15em] focus:border-white focus:outline-none">
            {STATUS_OPTS.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
          </select>
          <select value={draft.category} onChange={(e) => set("category", e.target.value)}
            data-testid={`product-category-${p.slug}`}
            className="bg-black border border-white/12 px-3 py-2 font-mono text-xs tracking-[0.15em] focus:border-white focus:outline-none">
            {(categories && categories.length ? categories : [{slug: draft.category, label: draft.category.toUpperCase()}]).map((c) => (
              <option key={c.slug} value={c.slug}>{c.label}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] text-white/50 cursor-pointer">
            <input type="checkbox" checked={draft.active} onChange={(e) => set("active", e.target.checked)} className="accent-white" data-testid={`product-active-${p.slug}`} />
            ACTIVE
          </label>
        </div>
      </div>

      <textarea value={draft.tagline} onChange={(e) => set("tagline", e.target.value)}
        className="mt-3 w-full bg-transparent border border-white/12 px-3 py-2 text-sm text-white/70 focus:border-white focus:outline-none" rows={2} />

      <div className="mt-4">
        <label className={labelCls}>PRODUCT IMAGE</label>
        <div className="mt-2"><ImagePicker value={draft.image} onChange={(v) => set("image", v)} tid={p.slug} /></div>
      </div>

      <div className="mt-4 flex flex-wrap gap-4">
        {draft.tiers.map((t) => (
          <div key={t.tier} className="border border-white/[0.07] p-2">
            <label className="font-mono text-[9px] tracking-[0.15em] text-white/40">{TIER_LABELS[t.tier] || t.tier} ($)</label>
            <div className="mt-1 flex gap-2">
              <input type="number" step="0.01" value={t.price}
                onChange={(e) => setTier(t.tier, e.target.value)}
                data-testid={`product-price-${p.slug}-${t.tier}`}
                className="w-24 bg-transparent border border-white/12 px-2 py-2 font-mono text-sm focus:border-white focus:outline-none" />
              <input type="number" min="0" max="90" step="1" value={t.sale_pct ?? 0}
                onChange={(e) => setTierSale(t.tier, e.target.value)}
                data-testid={`product-tier-sale-${p.slug}-${t.tier}`}
                title="Sale % for this duration only"
                className="w-20 bg-transparent border border-white/12 px-2 py-2 font-mono text-sm focus:border-white focus:outline-none" />
            </div>
            <div className="mt-1 font-mono text-[8px] tracking-[0.12em] text-white/25">PRICE · SALE % THIS DURATION</div>
          </div>
        ))}
        <div>
          <label className="font-mono text-[9px] tracking-[0.15em] text-white/40">KEYS SHOWN (-1 = AUTO POOL)</label>
          <input type="number" value={draft.stock_override ?? ""} placeholder={String(p.stock)}
            onChange={(e) => set("stock_override", e.target.value)}
            data-testid={`product-stock-${p.slug}`}
            className="mt-1 w-24 bg-transparent border border-white/12 px-2 py-2 font-mono text-sm focus:border-white focus:outline-none" />
        </div>
        <div>
          <label className="font-mono text-[9px] tracking-[0.15em] text-white/40">VOUCHES SHOWN (-1 = AUTO DISCORD)</label>
          <input type="number" value={draft.vouch_override ?? ""} placeholder={String(p.vouch_count ?? 0)}
            onChange={(e) => set("vouch_override", e.target.value)}
            data-testid={`product-vouches-${p.slug}`}
            className="mt-1 w-24 bg-transparent border border-white/12 px-2 py-2 font-mono text-sm focus:border-white focus:outline-none" />
        </div>
        <div>
          <label className="font-mono text-[9px] tracking-[0.15em] text-white/40">ALL-DURATIONS SALE % (0 = USE PER-DURATION)</label>
          <input type="number" min="0" max="90" step="1" value={draft.sale_pct ?? 0} onChange={(e) => set("sale_pct", e.target.value)}
            data-testid={`product-sale-${p.slug}`}
            className="mt-1 w-24 bg-transparent border border-white/12 px-2 py-2 font-mono text-sm focus:border-white focus:outline-none" />
        </div>
        <div>
          <label className="font-mono text-[9px] tracking-[0.15em] text-white/40">SALE ENDS (APPLIES TO ACTIVE SALES)</label>
          <input type="datetime-local" value={isoToLocal(draft.sale_ends_at)}
            onChange={(e) => set("sale_ends_at", e.target.value ? new Date(e.target.value).toISOString() : "")}
            data-testid={`product-sale-ends-${p.slug}`}
            className="mt-1 bg-transparent border border-white/12 px-2 py-2 font-mono text-xs focus:border-white focus:outline-none [color-scheme:dark]" />
        </div>
        <div>
          <label className="font-mono text-[9px] tracking-[0.15em] text-white/40">UNDETECTED SINCE (STREAK)</label>
          <input type="date" value={(draft.undetected_since || "").slice(0, 10)}
            onChange={(e) => set("undetected_since", e.target.value)}
            data-testid={`product-undetected-since-${p.slug}`}
            className="mt-1 bg-transparent border border-white/12 px-2 py-2 font-mono text-xs focus:border-white focus:outline-none [color-scheme:dark]" />
        </div>
        <div className="sm:col-span-2">
          <label className="font-mono text-[9px] tracking-[0.15em] text-white/40">LOADER LINK (CHEATS — GOES IN DELIVERY EMAIL)</label>
          <input type="url" value={draft.loader_url || ""} placeholder="https://..."
            onChange={(e) => set("loader_url", e.target.value)}
            data-testid={`product-loader-url-${p.slug}`}
            className="mt-1 w-full bg-transparent border border-white/12 px-2 py-2 font-mono text-xs focus:border-white focus:outline-none" />
        </div>
        <div className="sm:col-span-2">
          <label className="font-mono text-[9px] tracking-[0.15em] text-white/40">SETUP TUTORIAL LINK (CHEATS — GOES IN DELIVERY EMAIL)</label>
          <input type="url" value={draft.tutorial_url || ""} placeholder="https://..."
            onChange={(e) => set("tutorial_url", e.target.value)}
            data-testid={`product-tutorial-url-${p.slug}`}
            className="mt-1 w-full bg-transparent border border-white/12 px-2 py-2 font-mono text-xs focus:border-white focus:outline-none" />
        </div>
        <label className="flex items-end gap-2 font-mono text-[10px] tracking-[0.15em] text-white/50 cursor-pointer pb-2">
          <input type="checkbox" checked={draft.manage_stock} onChange={(e) => set("manage_stock", e.target.checked)} className="accent-white" data-testid={`product-managestock-${p.slug}`} />
          USE KEY POOL
        </label>
      </div>

      {sale && (
        <p className="mt-3 font-mono text-[10px] tracking-[0.15em] text-white/50" data-testid={`sale-note-${p.slug}`}>
          // SALE LIVE: {tierSaleCount > 0 ? `${tierSaleCount} SELECTED DURATION${tierSaleCount === 1 ? "" : "S"} DISCOUNTED` : `EVERY DURATION SHOWS ${parseFloat(draft.sale_pct)}% OFF`}{draft.sale_ends_at ? ` · ENDS ${new Date(draft.sale_ends_at).toLocaleString()} · LIVE COUNTDOWN SHOWN TO BUYERS` : " · NO END DATE SET"}
        </p>
      )}

      <div className="mt-5 flex flex-wrap gap-3">
        <button onClick={save} disabled={saving} data-testid={`save-product-${p.slug}`}
          className="btn-inj px-6 py-3 text-xs inline-flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} SAVE
        </button>
        <button onClick={() => setShowKeys(!showKeys)} data-testid={`keys-toggle-${p.slug}`}
          className="btn-ghost px-6 py-3 text-xs inline-flex items-center gap-2">
          <KeyRound size={14} /> KEYS {showKeys ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
        <button onClick={del} data-testid={`delete-product-${p.slug}`}
          className={`px-6 py-3 text-xs inline-flex items-center gap-2 border font-mono tracking-[0.15em] transition-colors ${
            confirmDel ? "border-red-500 text-red-400" : "border-white/15 text-white/50 hover:text-red-400 hover:border-red-500/50"
          }`}>
          <Trash2 size={14} /> {confirmDel ? "CONFIRM DELETE?" : "DELETE"}
        </button>
      </div>

      {showKeys && <KeysPanel product={p} />}
    </div>
  );
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-5" data-testid="admin-products">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="max-w-xl text-white/50 text-sm">
          <span className="font-mono text-white">ADD PRODUCT</span> creates a new game/product from scratch — no need to touch categories.{" "}
          Edit prices, images, status, run a <span className="font-mono text-white">SALE %</span>, manage <span className="font-mono text-white">KEYS</span>, or <span className="font-mono text-white">DELETE</span> a product entirely.
        </p>
        <button onClick={() => setAdding(!adding)} data-testid="add-product-toggle-btn"
          className="btn-inj px-6 py-3 text-xs inline-flex items-center gap-2">
          {adding ? <X size={14} /> : <Plus size={14} />} ADD PRODUCT
        </button>
      </div>
      {adding && <AddProductForm categories={categories} onCreated={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />}
      {products.map((p) => <ProductRow key={p.slug} p={p} categories={categories} onSaved={load} />)}
    </div>
  );
}
